import {
	forwardRef,
	Ref,
	RefObject,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import { HubAvatar } from "../../HubAvatar/HubAvatar";
import { HubCommentModel, HubCommentSaveModel } from "client/Api/Hub/common";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import {
	Form,
	Toolbar,
	ValidationError,
	useMentionInputIntermediateState,
	MentionInputIntermediateStateHandlers
} from "client/Components";
import styles from "./ReplySection.module.less";
import { localize } from "client_legacy/ndaw/culture";
import {
	CommentFormData,
	createFormDataForReply,
	createSaveModel,
	EMPTY_COMMENT_FORM_DATA,
	validationConfiguration
} from "../CommentForm/CommentForm";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { getModel } from "client/Components/MentionInput/converters";
import { isProseMirror } from "client/Components/MentionInput/types";
import { HubCommentSectionService } from "../HubCommentSection";
import { CommentComposer, CommentComposerHandle } from "./CommentComposer";
import { HubCommentServerError, tryParseHubCommentServerError } from "../MobileComment/MobileCommentForm/MobileCommentForm";
import { HubCommentOptionsModel } from "client/Api/Hub/common.details";
import { isNotNil } from "client/Misc/utils";

type ReplySectionHandle = {
	focus: () => void;
};

type ReplySectionProps = {
	service: HubCommentSectionService;
	commentId: number;
	parentId: number | null;
	commentAuthorUserInfo: UserIdentityModel;
	currentUserInfo: UserIdentityModel;
	options: HubCommentOptionsModel;
	onReplyCreated: (comment: HubCommentModel) => void;
	onCancel: () => void;
};

export const ReplySection = forwardRef((props: ReplySectionProps, ref: Ref<ReplySectionHandle>) => {
	const {
		service,
		currentUserInfo,
		commentId,
		commentAuthorUserInfo,
		onCancel,
		onReplyCreated,
		parentId,
		options
	} = props;

	const [formData, setFormData] = useState<CommentFormData>(
		() => createFormDataForReply(
			commentAuthorUserInfo,
			currentUserInfo.userId
		)
	);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const commentComposerRef: RefObject<CommentComposerHandle | null> = useRef<CommentComposerHandle>(null);

	const intermediateState: MentionInputIntermediateStateHandlers = useMentionInputIntermediateState();

	const formHandler: IFormHandler<CommentFormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	useImperativeHandle(
		ref,
		(): ReplySectionHandle => ({
			focus: () => commentComposerRef?.current
				?.focus()
		}),
		[]
	);

	const cancelClickHandler = (): void => {
		setFormData(EMPTY_COMMENT_FORM_DATA);
		onCancel();
	};

	const sendReply = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);
			try {
				const validationState: InputValidationResult = await formHandler
					.validation
					.validate(globalCancellation);

				if (validationState.kind === InputValidationResultKind.Invalid) {
					setIsBusy(false);
					return;
				}

				const model: HubCommentSaveModel = createSaveModel(formData);

				const saveResult: HubCommentModel = await service.createReply(
					parentId ?? commentId,
					model
				);

				onReplyCreated(saveResult);
				setFormData(EMPTY_COMMENT_FORM_DATA);
				setIsBusy(false);
				onCancel();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (formHandler.validation.tryProcessServerError(e)) {
					const result: TryResult<HubCommentServerError> = tryParseHubCommentServerError(e);
					if (!result.wasSuccessful) {
						return;
					}

					const mediaResult: string | undefined = result.result
						.media;

					if (isNotNil(mediaResult)) {
						alertManager.showError(mediaResult);

						formHandler.for("media")
							.reducer
							.setValue({
								isSet: true,
								value: null
							});

						formHandler.for("media")
							.validation
							.triggerValueChanged();
					}
				} else {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			globalCancellation,
			isBusy,
			formHandler,
			service,
			commentId,
			parentId,
			onReplyCreated,
			onCancel
		]
	);

	const isEmpty = useMemo(
		(): boolean => isProseMirror(intermediateState.intermediateState)
			&& getModel(intermediateState.intermediateState.editorState)
				.text
				.length === 0,
		[intermediateState]
	);

	const canSave = useMemo(
		(): boolean => {
			if (isBusy) {
				return false;
			}

			if (
				isEmpty
					&& formData.media.isSet
					&& formData.media.value === null
			) {
				return false;
			}

			return true;
		},
		[
			formData,
			isBusy,
			isEmpty
		]
	);

	return (
		<Form onSubmit={sendReply}>
			<div className={styles.root}>
				<div className={styles.avatarWrapper}>
					<HubAvatar authorUserInfo={currentUserInfo} />
				</div>
				<div className={styles.message}>
					<CommentComposer
						{...formHandler.for("message").attachToMentionInput(intermediateState, formData.mentionData)}
						trackingId="reply-comment-composer"
						placeholder={localize("_WriteYourReply")}
						ref={commentComposerRef}
						autoFocus="focus"
						onFormDataChange={setFormData}
						formData={formData}
						service={service}
						options={options}
					/>
					<ValidationError
						{...formHandler.for("message").attachToValidationError()}
					/>
				</div>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Post")}
						isDisabled={!canSave}
						onClick={sendReply}
						trackingId="submit-reply"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						isDisabled={isBusy}
						onClick={cancelClickHandler}
						trackingId="cancel-reply"
					/>
				</Toolbar>
			</div>
		</Form>
	);
});

ReplySection.displayName = "ReplySection";
